"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.continueTrace = void 0;
exports.startInactiveSpan = startInactiveSpan;
exports.startNewTrace = startNewTrace;
exports.startSpan = startSpan;
exports.startSpanManual = startSpanManual;
exports.suppressTracing = suppressTracing;
exports.withActiveSpan = withActiveSpan;

var _carrier = require("../carrier.js");

var _currentScopes = require("../currentScopes.js");

var _index = require("../asyncContext/index.js");

var _debugBuild = require("../debug-build.js");

var _semanticAttributes = require("../semanticAttributes.js");

var _logger = require("../utils-hoist/logger.js");

var _propagationContext = require("../utils-hoist/propagationContext.js");

var _tracing = require("../utils-hoist/tracing.js");

var _handleCallbackErrors = require("../utils/handleCallbackErrors.js");

var _hasTracingEnabled = require("../utils/hasTracingEnabled.js");

var _spanOnScope = require("../utils/spanOnScope.js");

var _spanUtils = require("../utils/spanUtils.js");

var _dynamicSamplingContext = require("./dynamicSamplingContext.js");

var _logSpans = require("./logSpans.js");

var _sampling = require("./sampling.js");

var _sentryNonRecordingSpan = require("./sentryNonRecordingSpan.js");

var _sentrySpan = require("./sentrySpan.js");

var _spanstatus = require("./spanstatus.js");

var _utils = require("./utils.js");

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

const SUPPRESS_TRACING_KEY = '__SENTRY_SUPPRESS_TRACING__';
/**
 * Wraps a function with a transaction/span and finishes the span after the function is done.
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getActiveSpan()`, as long as the function is executed while the scope is active.
 *
 * If you want to create a span that is not set as active, use {@link startInactiveSpan}.
 *
 * You'll always get a span passed to the callback,
 * it may just be a non-recording span if the span is not sampled or if tracing is disabled.
 */

function startSpan(options, callback) {
  const acs = getAcs();

  if (acs.startSpan) {
    return acs.startSpan(options, callback);
  }

  const spanArguments = parseSentrySpanArguments(options);
  const forceTransaction = options.forceTransaction,
        customParentSpan = options.parentSpan;
  return (0, _currentScopes.withScope)(options.scope, () => {
    // If `options.parentSpan` is defined, we want to wrap the callback in `withActiveSpan`
    const wrapper = getActiveSpanWrapper(customParentSpan);
    return wrapper(() => {
      const scope = (0, _currentScopes.getCurrentScope)();
      const parentSpan = getParentSpan(scope);
      const shouldSkipSpan = options.onlyIfParent && !parentSpan;
      const activeSpan = shouldSkipSpan ? new _sentryNonRecordingSpan.SentryNonRecordingSpan() : createChildOrRootSpan({
        parentSpan,
        spanArguments,
        forceTransaction,
        scope
      });
      (0, _spanOnScope._setSpanForScope)(scope, activeSpan);
      return (0, _handleCallbackErrors.handleCallbackErrors)(() => callback(activeSpan), () => {
        // Only update the span status if it hasn't been changed yet, and the span is not yet finished
        const _spanToJSON = (0, _spanUtils.spanToJSON)(activeSpan),
              status = _spanToJSON.status;

        if (activeSpan.isRecording() && (!status || status === 'ok')) {
          activeSpan.setStatus({
            code: _spanstatus.SPAN_STATUS_ERROR,
            message: 'internal_error'
          });
        }
      }, () => activeSpan.end());
    });
  });
}
/**
 * Similar to `Sentry.startSpan`. Wraps a function with a transaction/span, but does not finish the span
 * after the function is done automatically. You'll have to call `span.end()` manually.
 *
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getActiveSpan()`, as long as the function is executed while the scope is active.
 *
 * You'll always get a span passed to the callback,
 * it may just be a non-recording span if the span is not sampled or if tracing is disabled.
 */


function startSpanManual(options, callback) {
  const acs = getAcs();

  if (acs.startSpanManual) {
    return acs.startSpanManual(options, callback);
  }

  const spanArguments = parseSentrySpanArguments(options);
  const forceTransaction = options.forceTransaction,
        customParentSpan = options.parentSpan;
  return (0, _currentScopes.withScope)(options.scope, () => {
    // If `options.parentSpan` is defined, we want to wrap the callback in `withActiveSpan`
    const wrapper = getActiveSpanWrapper(customParentSpan);
    return wrapper(() => {
      const scope = (0, _currentScopes.getCurrentScope)();
      const parentSpan = getParentSpan(scope);
      const shouldSkipSpan = options.onlyIfParent && !parentSpan;
      const activeSpan = shouldSkipSpan ? new _sentryNonRecordingSpan.SentryNonRecordingSpan() : createChildOrRootSpan({
        parentSpan,
        spanArguments,
        forceTransaction,
        scope
      });
      (0, _spanOnScope._setSpanForScope)(scope, activeSpan);

      function finishAndSetSpan() {
        activeSpan.end();
      }

      return (0, _handleCallbackErrors.handleCallbackErrors)(() => callback(activeSpan, finishAndSetSpan), () => {
        // Only update the span status if it hasn't been changed yet, and the span is not yet finished
        const _spanToJSON2 = (0, _spanUtils.spanToJSON)(activeSpan),
              status = _spanToJSON2.status;

        if (activeSpan.isRecording() && (!status || status === 'ok')) {
          activeSpan.setStatus({
            code: _spanstatus.SPAN_STATUS_ERROR,
            message: 'internal_error'
          });
        }
      });
    });
  });
}
/**
 * Creates a span. This span is not set as active, so will not get automatic instrumentation spans
 * as children or be able to be accessed via `Sentry.getActiveSpan()`.
 *
 * If you want to create a span that is set as active, use {@link startSpan}.
 *
 * This function will always return a span,
 * it may just be a non-recording span if the span is not sampled or if tracing is disabled.
 */


function startInactiveSpan(options) {
  const acs = getAcs();

  if (acs.startInactiveSpan) {
    return acs.startInactiveSpan(options);
  }

  const spanArguments = parseSentrySpanArguments(options);
  const forceTransaction = options.forceTransaction,
        customParentSpan = options.parentSpan; // If `options.scope` is defined, we use this as as a wrapper,
  // If `options.parentSpan` is defined, we want to wrap the callback in `withActiveSpan`

  const wrapper = options.scope ? callback => (0, _currentScopes.withScope)(options.scope, callback) : customParentSpan !== undefined ? callback => withActiveSpan(customParentSpan, callback) : callback => callback();
  return wrapper(() => {
    const scope = (0, _currentScopes.getCurrentScope)();
    const parentSpan = getParentSpan(scope);
    const shouldSkipSpan = options.onlyIfParent && !parentSpan;

    if (shouldSkipSpan) {
      return new _sentryNonRecordingSpan.SentryNonRecordingSpan();
    }

    return createChildOrRootSpan({
      parentSpan,
      spanArguments,
      forceTransaction,
      scope
    });
  });
}
/**
 * Continue a trace from `sentry-trace` and `baggage` values.
 * These values can be obtained from incoming request headers, or in the browser from `<meta name="sentry-trace">`
 * and `<meta name="baggage">` HTML tags.
 *
 * Spans started with `startSpan`, `startSpanManual` and `startInactiveSpan`, within the callback will automatically
 * be attached to the incoming trace.
 */


const continueTrace = (options, callback) => {
  const carrier = (0, _carrier.getMainCarrier)();
  const acs = (0, _index.getAsyncContextStrategy)(carrier);

  if (acs.continueTrace) {
    return acs.continueTrace(options, callback);
  }

  const sentryTrace = options.sentryTrace,
        baggage = options.baggage;
  return (0, _currentScopes.withScope)(scope => {
    const propagationContext = (0, _tracing.propagationContextFromHeaders)(sentryTrace, baggage);
    scope.setPropagationContext(propagationContext);
    return callback();
  });
};
/**
 * Forks the current scope and sets the provided span as active span in the context of the provided callback. Can be
 * passed `null` to start an entirely new span tree.
 *
 * @param span Spans started in the context of the provided callback will be children of this span. If `null` is passed,
 * spans started within the callback will not be attached to a parent span.
 * @param callback Execution context in which the provided span will be active. Is passed the newly forked scope.
 * @returns the value returned from the provided callback function.
 */


exports.continueTrace = continueTrace;

function withActiveSpan(span, callback) {
  const acs = getAcs();

  if (acs.withActiveSpan) {
    return acs.withActiveSpan(span, callback);
  }

  return (0, _currentScopes.withScope)(scope => {
    (0, _spanOnScope._setSpanForScope)(scope, span || undefined);
    return callback(scope);
  });
}
/** Suppress tracing in the given callback, ensuring no spans are generated inside of it. */


function suppressTracing(callback) {
  const acs = getAcs();

  if (acs.suppressTracing) {
    return acs.suppressTracing(callback);
  }

  return (0, _currentScopes.withScope)(scope => {
    scope.setSDKProcessingMetadata({
      [SUPPRESS_TRACING_KEY]: true
    });
    return callback();
  });
}
/**
 * Starts a new trace for the duration of the provided callback. Spans started within the
 * callback will be part of the new trace instead of a potentially previously started trace.
 *
 * Important: Only use this function if you want to override the default trace lifetime and
 * propagation mechanism of the SDK for the duration and scope of the provided callback.
 * The newly created trace will also be the root of a new distributed trace, for example if
 * you make http requests within the callback.
 * This function might be useful if the operation you want to instrument should not be part
 * of a potentially ongoing trace.
 *
 * Default behavior:
 * - Server-side: A new trace is started for each incoming request.
 * - Browser: A new trace is started for each page our route. Navigating to a new route
 *            or page will automatically create a new trace.
 */


function startNewTrace(callback) {
  return (0, _currentScopes.withScope)(scope => {
    scope.setPropagationContext({
      traceId: (0, _propagationContext.generateTraceId)()
    });
    _debugBuild.DEBUG_BUILD && _logger.logger.info(`Starting a new trace with id ${scope.getPropagationContext().traceId}`);
    return withActiveSpan(null, callback);
  });
}

function createChildOrRootSpan({
  parentSpan,
  spanArguments,
  forceTransaction,
  scope
}) {
  if (!(0, _hasTracingEnabled.hasTracingEnabled)()) {
    return new _sentryNonRecordingSpan.SentryNonRecordingSpan();
  }

  const isolationScope = (0, _currentScopes.getIsolationScope)();
  let span;

  if (parentSpan && !forceTransaction) {
    span = _startChildSpan(parentSpan, scope, spanArguments);
    (0, _spanUtils.addChildSpanToSpan)(parentSpan, span);
  } else if (parentSpan) {
    // If we forced a transaction but have a parent span, make sure to continue from the parent span, not the scope
    const dsc = (0, _dynamicSamplingContext.getDynamicSamplingContextFromSpan)(parentSpan);

    const _parentSpan$spanConte = parentSpan.spanContext(),
          traceId = _parentSpan$spanConte.traceId,
          parentSpanId = _parentSpan$spanConte.spanId;

    const parentSampled = (0, _spanUtils.spanIsSampled)(parentSpan);
    span = _startRootSpan(_objectSpread({
      traceId,
      parentSpanId
    }, spanArguments), scope, parentSampled);
    (0, _dynamicSamplingContext.freezeDscOnSpan)(span, dsc);
  } else {
    const _isolationScope$getPr = _objectSpread(_objectSpread({}, isolationScope.getPropagationContext()), scope.getPropagationContext()),
          traceId = _isolationScope$getPr.traceId,
          dsc = _isolationScope$getPr.dsc,
          parentSpanId = _isolationScope$getPr.parentSpanId,
          parentSampled = _isolationScope$getPr.sampled;

    span = _startRootSpan(_objectSpread({
      traceId,
      parentSpanId
    }, spanArguments), scope, parentSampled);

    if (dsc) {
      (0, _dynamicSamplingContext.freezeDscOnSpan)(span, dsc);
    }
  }

  (0, _logSpans.logSpanStart)(span);
  (0, _utils.setCapturedScopesOnSpan)(span, scope, isolationScope);
  return span;
}
/**
 * This converts StartSpanOptions to SentrySpanArguments.
 * For the most part (for now) we accept the same options,
 * but some of them need to be transformed.
 */


function parseSentrySpanArguments(options) {
  const exp = options.experimental || {};

  const initialCtx = _objectSpread({
    isStandalone: exp.standalone
  }, options);

  if (options.startTime) {
    const ctx = _objectSpread({}, initialCtx);

    ctx.startTimestamp = (0, _spanUtils.spanTimeInputToSeconds)(options.startTime);
    delete ctx.startTime;
    return ctx;
  }

  return initialCtx;
}

function getAcs() {
  const carrier = (0, _carrier.getMainCarrier)();
  return (0, _index.getAsyncContextStrategy)(carrier);
}

function _startRootSpan(spanArguments, scope, parentSampled) {
  const client = (0, _currentScopes.getClient)();
  const options = client && client.getOptions() || {};
  const _spanArguments$name = spanArguments.name,
        name = _spanArguments$name === void 0 ? '' : _spanArguments$name,
        attributes = spanArguments.attributes;

  const _ref = scope.getScopeData().sdkProcessingMetadata[SUPPRESS_TRACING_KEY] ? [false] : (0, _sampling.sampleSpan)(options, {
    name,
    parentSampled,
    attributes,
    transactionContext: {
      name,
      parentSampled
    }
  }),
        _ref2 = _slicedToArray(_ref, 2),
        sampled = _ref2[0],
        sampleRate = _ref2[1];

  const rootSpan = new _sentrySpan.SentrySpan(_objectSpread(_objectSpread({}, spanArguments), {}, {
    attributes: _objectSpread({
      [_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'custom'
    }, spanArguments.attributes),
    sampled
  }));

  if (sampleRate !== undefined) {
    rootSpan.setAttribute(_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_SAMPLE_RATE, sampleRate);
  }

  if (client) {
    client.emit('spanStart', rootSpan);
  }

  return rootSpan;
}
/**
 * Creates a new `Span` while setting the current `Span.id` as `parentSpanId`.
 * This inherits the sampling decision from the parent span.
 */


function _startChildSpan(parentSpan, scope, spanArguments) {
  const _parentSpan$spanConte2 = parentSpan.spanContext(),
        spanId = _parentSpan$spanConte2.spanId,
        traceId = _parentSpan$spanConte2.traceId;

  const sampled = scope.getScopeData().sdkProcessingMetadata[SUPPRESS_TRACING_KEY] ? false : (0, _spanUtils.spanIsSampled)(parentSpan);
  const childSpan = sampled ? new _sentrySpan.SentrySpan(_objectSpread(_objectSpread({}, spanArguments), {}, {
    parentSpanId: spanId,
    traceId,
    sampled
  })) : new _sentryNonRecordingSpan.SentryNonRecordingSpan({
    traceId
  });
  (0, _spanUtils.addChildSpanToSpan)(parentSpan, childSpan);
  const client = (0, _currentScopes.getClient)();

  if (client) {
    client.emit('spanStart', childSpan); // If it has an endTimestamp, it's already ended

    if (spanArguments.endTimestamp) {
      client.emit('spanEnd', childSpan);
    }
  }

  return childSpan;
}

function getParentSpan(scope) {
  const span = (0, _spanOnScope._getSpanForScope)(scope);

  if (!span) {
    return undefined;
  }

  const client = (0, _currentScopes.getClient)();
  const options = client ? client.getOptions() : {};

  if (options.parentSpanIsAlwaysRootSpan) {
    return (0, _spanUtils.getRootSpan)(span);
  }

  return span;
}

function getActiveSpanWrapper(parentSpan) {
  return parentSpan !== undefined ? callback => {
    return withActiveSpan(parentSpan, callback);
  } : callback => callback();
}